import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import ImageUploader from '../components/ImageUploader';
import BeforeAfterSlider from '../components/BeforeAfterSlider';
import { buildApiUrl } from '../config/api';
import { parseMakeupApiResponse } from '../utils/makeupApi';

export default function DemoPage() {
  const [isLoading, setIsLoading] = useState(false);
  const [beforeImage, setBeforeImage] = useState(null);
  const [afterImage, setAfterImage] = useState(null);
  const [showResults, setShowResults] = useState(false);
  const [error, setError] = useState('');

  // Demo-Funktion, um das Makeup zu "entfernen" (simuliert)
  const processImage = async (file) => {
    setIsLoading(true);
    setError('');

    // Speichere das originale Bild
    const reader = new FileReader();
    reader.onload = async () => {
      setBeforeImage(reader.result);

      // API-Aufruf an den Replicate-Proxy
      try {
        const formData = new FormData();
        formData.append('image', file);

        const response = await fetch(buildApiUrl('/api/remove-makeup'), {
          method: 'POST',
          body: formData,
        });

        const result = await parseMakeupApiResponse(response);

        if (result.type === 'image' || result.type === 'image-url') {
          setAfterImage(result.url);
          setShowResults(true);
          setIsLoading(false);
        } else {
          // Warte auf das Ergebnis der Verarbeitung
          const checkInterval = setInterval(async () => {
            const resultResponse = await fetch(result.url);
            const resultData = await resultResponse.json();

            if (resultData.status === 'succeeded' && resultData.output) {
              clearInterval(checkInterval);
              setAfterImage(resultData.output[0]); // Replicate gibt ein Array zurück
              setShowResults(true);
              setIsLoading(false);
            } else if (resultData.status === 'failed') {
              clearInterval(checkInterval);
              setError('KI-Verarbeitung fehlgeschlagen. Bitte versuche es erneut.');
              setIsLoading(false);
            }
          }, 3000); // Überprüfe alle 3 Sekunden
        }
      } catch (e) {
        setError(`Fehler beim Senden der Anfrage: ${e.message}`);
        setIsLoading(false);
      }
    };
    reader.onerror = () => {
      setError('Fehler beim Lesen des Bildes. Bitte versuche es erneut.');
      setIsLoading(false);
    };
    reader.readAsDataURL(file);
  };

  return (
    <div className="py-12 bg-gray-50 min-h-screen">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center">
          <h1 className="text-3xl font-extrabold text-gray-900 sm:text-4xl">
            Probiere NaturalMe kostenlos aus
          </h1>
          <p className="mt-4 text-xl text-gray-600 max-w-2xl mx-auto">
            Lade ein Bild mit geschminktem Gesicht hoch und lass unsere KI zeigen, wie du natürlich ohne Make-up aussehen könntest.
          </p>
        </div>

        <div className="mt-12 max-w-lg mx-auto">
          <div className="bg-white rounded-lg shadow-xl overflow-hidden">
            <div className="p-6">
              {!showResults ? (
                <>
                  <h2 className="text-lg font-medium text-gray-900 mb-4">Lade ein Bild hoch</h2>
                  <ImageUploader onImageUpload={processImage} />
                  
                  {isLoading && (
                    <div className="mt-6 text-center">
                      <div className="inline-block animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-primary-600"></div>
                      <p className="mt-2 text-primary-600">Verarbeite Bild...</p>
                    </div>
                  )}
                  
                  {error && (
                    <div className="mt-4 text-red-500">
                      {error}
                    </div>
                  )}
                </>
              ) : (
                <>
                  <h2 className="text-lg font-medium text-gray-900 mb-4">Dein Ergebnis</h2>
                  
                  <BeforeAfterSlider 
                    beforeImage={beforeImage}
                    afterImage={afterImage}
                  />
                  
                  <div className="mt-6 space-y-4">
                    <p className="text-sm text-gray-500">
                      Hinweis: Dies ist eine Demo-Version mit begrenzter Genauigkeit. Die Vollversion bietet präzisere Ergebnisse.
                    </p>
                    <div className="flex space-x-4">
                      <button
                        onClick={() => setShowResults(false)}
                        className="flex-1 py-2 px-4 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                      >
                        Neues Bild hochladen
                      </button>
                      <Link
                        to="/pricing"
                        className="flex-1 py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                      >
                        Premium-Version testen
                      </Link>
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>

        <div className="mt-16">
          <div className="bg-white shadow overflow-hidden sm:rounded-lg">
            <div className="px-4 py-5 sm:px-6">
              <h2 className="text-lg leading-6 font-medium text-gray-900">
                Häufig gestellte Fragen
              </h2>
              <p className="mt-1 max-w-2xl text-sm text-gray-500">
                Mehr Informationen über unsere Makeup-Entfernungs-Technologie
              </p>
            </div>
            <div className="border-t border-gray-200">
              <dl>
                <div className="bg-gray-50 px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                  <dt className="text-sm font-medium text-gray-500">
                    Wie genau ist die Technologie?
                  </dt>
                  <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                    Unsere KI-Technologie basiert auf fortschrittlichem Deep Learning und wurde mit tausenden von Bildern trainiert. Die Demo-Version bietet eine grundlegende Funktion, während die Premium-Version eine deutlich höhere Genauigkeit aufweist.
                  </dd>
                </div>
                <div className="bg-white px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                  <dt className="text-sm font-medium text-gray-500">
                    Wie werden meine Bilder verarbeitet?
                  </dt>
                  <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                    Deine Bilder werden lokal in deinem Browser verarbeitet und nicht auf unseren Servern gespeichert. Bei der Premium-Version werden Bilder temporär auf sicheren Servern verarbeitet und anschließend automatisch gelöscht.
                  </dd>
                </div>
                <div className="bg-gray-50 px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                  <dt className="text-sm font-medium text-gray-500">
                    Funktioniert es mit allen Bildern?
                  </dt>
                  <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                    Die besten Ergebnisse werden mit Frontalaufnahmen bei guter Beleuchtung erzielt. Starke Schatten, extreme Winkel oder sehr intensives Make-up können die Ergebnisse beeinträchtigen.
                  </dd>
                </div>
                <div className="bg-white px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                  <dt className="text-sm font-medium text-gray-500">
                    Welche Arten von Make-up kann entfernt werden?
                  </dt>
                  <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                    Unsere KI kann Foundation, Rouge, Lidschatten, Eyeliner, Mascara, Lippenstift und Konturierung erkennen und entfernen. Künstliche Wimpern und stark bearbeitete Bilder stellen jedoch eine größere Herausforderung dar.
                  </dd>
                </div>
              </dl>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
